import { useState, useEffect, useMemo } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/products/ProductCard";
import { ProductFilters, FilterState } from "@/components/products/ProductFilters";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, Loader2, SlidersHorizontal, X, Search } from "lucide-react";
import { fetchProducts, Product } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";

const Products = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("lowest");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>({
    category: "all",
    status: "all",
    type: "all",
    minPrice: "",
    maxPrice: "",
    bestSellerOnly: false,
  });

  useEffect(() => {
    async function loadProducts() {
      setLoading(true);
      const data = await fetchProducts();
      setProducts(data);
      setLoading(false);
    }
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!product.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Category filter
      if (filters.category !== "all" && product.categorySlug !== filters.category) {
        return false;
      }

      // Status filter
      if (filters.status !== "all") {
        if (filters.status === "available" && product.stock !== "available") return false;
        if (filters.status === "outOfStock" && product.stock !== "outOfStock") return false;
      }

      // Type filter - map display type back to subscription type
      if (filters.type !== "all") {
        const typeMap: Record<string, string> = {
          private: "Private",
          sharing_1p1u: "Sharing 1P1U",
          sharing: "Sharing",
          invite: "Invite",
          family_head: "Family Head",
          family_plan_invite: "Family Plan Invite",
          voucher: "Voucher",
          individual: "Individual",
        };
        if (product.type !== typeMap[filters.type]) return false;
      }

      // Price range filter
      if (filters.minPrice && product.price < parseInt(filters.minPrice)) {
        return false;
      }
      if (filters.maxPrice && product.price > parseInt(filters.maxPrice)) {
        return false;
      }

      // Best seller filter
      if (filters.bestSellerOnly && !product.isBestSeller) {
        return false;
      }

      return true;
    });
  }, [products, filters, searchQuery]);

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      if (sortBy === "lowest") return a.price - b.price;
      if (sortBy === "highest") return b.price - a.price;
      return 0;
    });
  }, [filteredProducts, sortBy]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.category !== "all") count++;
    if (filters.status !== "all") count++;
    if (filters.type !== "all") count++;
    if (filters.minPrice) count++;
    if (filters.maxPrice) count++;
    if (filters.bestSellerOnly) count++;
    return count;
  }, [filters]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 py-8">
        <div className="container">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <Badge className="gradient-primary text-primary-foreground border-0 px-4 py-2 text-sm">
              <Package className="h-4 w-4 mr-2" />
              Semua Aplikasi - {products.length} total
            </Badge>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Cari aplikasi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-card"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            {/* Filters Sidebar - Desktop */}
            <div className="hidden lg:block lg:col-span-1">
              <ProductFilters filters={filters} onFilterChange={setFilters} />
            </div>

            {/* Products Grid */}
            <div className="lg:col-span-3 space-y-6">
              {/* Sort Header */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {/* Mobile Filter Button */}
                  <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm" className="lg:hidden gap-2">
                        <SlidersHorizontal className="h-4 w-4" />
                        Filter
                        {activeFiltersCount > 0 && (
                          <Badge className="h-5 w-5 p-0 flex items-center justify-center bg-primary text-primary-foreground">
                            {activeFiltersCount}
                          </Badge>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-[300px] sm:w-[350px] overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle>Filter Produk</SheetTitle>
                      </SheetHeader>
                      <div className="mt-4">
                        <ProductFilters filters={filters} onFilterChange={setFilters} />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <p className="text-sm text-muted-foreground">
                    Menampilkan <span className="font-medium text-foreground">{sortedProducts.length}</span> dari {products.length} produk
                  </p>
                </div>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40 sm:w-48 bg-card">
                    <SelectValue placeholder="Urutkan" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border">
                    <SelectItem value="lowest">Harga Terendah</SelectItem>
                    <SelectItem value="highest">Harga Tertinggi</SelectItem>
                    <SelectItem value="newest">Terbaru</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Active Filters Tags */}
              {activeFiltersCount > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filters.category !== "all" && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      Kategori: {filters.category}
                      <button
                        onClick={() => setFilters({ ...filters, category: "all" })}
                        className="ml-1 hover:bg-muted rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.bestSellerOnly && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      Best Seller
                      <button
                        onClick={() => setFilters({ ...filters, bestSellerOnly: false })}
                        className="ml-1 hover:bg-muted rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  {filters.type !== "all" && (
                    <Badge variant="secondary" className="gap-1 pr-1">
                      Tipe: {filters.type}
                      <button
                        onClick={() => setFilters({ ...filters, type: "all" })}
                        className="ml-1 hover:bg-muted rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground"
                    onClick={() => setFilters({
                      category: "all",
                      status: "all",
                      type: "all",
                      minPrice: "",
                      maxPrice: "",
                      bestSellerOnly: false,
                    })}
                  >
                    Hapus Semua
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                  {sortedProducts.map((product, index) => (
                    <div
                      key={product.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              )}

              {!loading && sortedProducts.length === 0 && (
                <div className="text-center py-20">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">Tidak ada produk yang cocok</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters({
                      category: "all",
                      status: "all",
                      type: "all",
                      minPrice: "",
                      maxPrice: "",
                      bestSellerOnly: false,
                    })}
                  >
                    Reset Filter
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Products;